/**
 * Where the clinic chooser lives, from wherever the browser is now.
 *
 * ── Why anything needs this ─────────────────────────────────────────────────
 *
 * The chooser is an apex-only screen, and not by choice of layout: the API
 * resolves the tenant from the subdomain, so a sign-in served at
 * `their-practice.dentacore.uk` is scoped to that practice by construction and
 * can never offer a choice. Somebody who works at two therefore has to be
 * *taken* to the apex to switch — see `signOut` in `auth-context.tsx` for why
 * that is the moment we do it.
 *
 * ── The rule, and the one deployment shape it cannot infer ──────────────────
 *
 * Strip the leading label. `cedar.dentacore.uk` → `dentacore.uk`, and
 * `cedar.localhost:5173` → `localhost:5173`, which is what makes the whole
 * subdomain model developable locally.
 *
 * **It cannot tell `cedar.dentacore.io` served from an apex of `dentacore.io`
 * apart from one served from `app.dentacore.io`** — `clinicOrigin` on the API
 * side *replaces* a leading `app.` when it builds the forward direction, and
 * that replacement is not reversible from the hostname alone. Every current
 * deployment is the first shape. If the second is ever adopted, set
 * `VITE_APEX_ORIGIN` and this stops guessing; that is deliberately an override
 * rather than a required setting, because `clinic-url.ts` argues at length
 * against two values that have to agree and are only exercised in production.
 */
const CONFIGURED = (import.meta.env.VITE_APEX_ORIGIN as string | undefined)?.trim();

/**
 * The apex origin, or **null when this browser is already at it** — a bare
 * `localhost`, an IP address, a `www.` host, or a two-label domain. Null means
 * "nowhere to go", not "unknown", so callers treat it as "stay put" rather than
 * as a failure.
 */
export function apexOrigin(
  hostname: string = window.location.hostname,
  protocol: string = window.location.protocol,
  port: string = window.location.port,
): string | null {
  if (CONFIGURED !== undefined && CONFIGURED.length > 0) return CONFIGURED;

  // An IP address has no subdomain to strip. A deployment reached this way is
  // single-tenant in practice, and there is no apex to send anybody to.
  if (/^\d+(\.\d+)*$/.test(hostname)) return null;

  // Same rule as `clinicSlug` in `api/client.ts` and `clinicSlugOf` on the API:
  // three labels, or two when the last is `localhost`. Fewer means this is
  // already the apex.
  const labels = hostname.split(".");
  const isLoopbackName = labels.at(-1) === "localhost";
  if (labels.length < (isLoopbackName ? 2 : 3)) return null;

  const first = labels[0];
  if (first === undefined || first.length === 0 || first === "www") return null;

  const apex = labels.slice(1).join(".");
  return `${protocol}//${apex}${port === "" ? "" : `:${port}`}`;
}
